import React from 'react';
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  FilePlus2,
  FileHeart,
  FileCheck,
  Share2,
} from 'lucide-react';
import { Card } from "@/components/ui/card";

const PDFGenerationLoader = ({ progress }) => {
  const generationSteps = [
    {
      icon: FileText,
      text: "Preparando el documento",
      detail: "Estructurando la información del análisis"
    },
    {
      icon: FilePlus2,
      text: "Agregando diagnóstico",
      detail: "Incluyendo detalles importantes sobre tu mascota"
    },
    {
      icon: FileHeart,
      text: "Personalizando recomendaciones",
      detail: "Generando guías específicas para tu mascota"
    },
    {
      icon: FileCheck,
      text: "Finalizando el informe",
      detail: "Verificando toda la información"
    },
    {
      icon: Share2,
      text: "Listo para compartir",
      detail: "Podrás compartirlo con tu veterinario de confianza"
    }
  ];

  const currentStep = Math.floor(progress / 20);

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-[#FAF9F6]/80 backdrop-blur-sm" />

      {/* Dialog positioning */}
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          {/* Dialog */}
          <Card className="relative max-w-lg w-full bg-white/80 backdrop-blur-md shadow-xl rounded-2xl">
            {/* Progress Bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-amber-100 overflow-hidden rounded-t-2xl">
              <motion.div
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                initial={{ width: "0%" }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>

            <div className="p-6 pt-8">
              {/* Header */}
              <div className="text-center mb-6">
                <motion.h2
                  className="text-lg font-semibold text-amber-600"
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  Generando tu Informe PDF...
                </motion.h2>
                <p className="text-sm text-gray-500 mt-1">
                  Estamos preparando un documento detallado para ti
                </p>
              </div>

              {/* Steps */}
              <div className="space-y-3">
                {generationSteps.map((step, index) => {
                  const isComplete = index < currentStep;
                  const isCurrent = index === currentStep;
                  
                  return (
                    <motion.div
                      key={step.text}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`
                        flex items-start gap-3 p-3 rounded-xl border
                        ${isComplete ? 'bg-amber-50 border-amber-100' : 'bg-white border-gray-100'}
                        ${isCurrent ? 'bg-amber-50/50 border-amber-200 ring-1 ring-amber-200' : ''}
                      `}
                    >
                      <div className={`
                        p-2 rounded-xl shrink-0
                        ${isComplete ? 'bg-amber-100' : 'bg-gray-50'}
                        ${isCurrent ? 'bg-amber-100' : ''}
                      `}>
                        <step.icon className={`
                          w-4 h-4
                          ${isComplete ? 'text-amber-500' : 'text-gray-400'}
                          ${isCurrent ? 'text-amber-600' : ''}
                        `} />
                      </div>
                      
                      <div>
                        <p className={`
                          text-sm font-medium
                          ${isComplete ? 'text-gray-900' : 'text-gray-500'}
                          ${isCurrent ? 'text-gray-900' : ''}
                        `}>
                          {step.text}
                        </p>
                        <p className={`
                          text-xs
                          ${isComplete ? 'text-gray-600' : 'text-gray-400'}
                          ${isCurrent ? 'text-gray-600' : ''}
                        `}>
                          {step.detail}
                        </p>
                      </div>

                      {isCurrent && (
                        <motion.div
                          className="ml-auto mt-1.5"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity }}
                        >
                          <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PDFGenerationLoader;